import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, FormGroup, Label, Input, Spinner } from 'reactstrap';
import neoLogo from '../../assets/images/Neo_Logo_C.svg';
import bgImage from '../../assets/images/pay.jpeg';
import { db } from '../../services/firebase';
import { collection, doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

const CheckoutPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [step, setStep] = useState(1);
  const [metodoPagamento, setMetodoPagamento] = useState('CARTAO'); // CARTAO, PIX, BOLETO
  const [tenantId, setTenantId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Scroll para o topo ao carregar
    window.scrollTo(0, 0);
    document.title = "Checkout | NeoApp";

    if (location.state && location.state.plan) {
      setPlan(location.state.plan);
    } else {
      // Se não tiver plano no state, volta pra home (comentado para testes locais, mas mantenha)
      navigate('/');
    }
  }, [location, navigate]);

  const [formData, setFormData] = useState({
    nome: '',
    documento: '', // CPF ou CNPJ
    nomeEmpresa: '', // Opcional, ou nome do personal
    email: '',
    telefone: '',
    senha: '',
    // dados cartao
    numeroCartao: '',
    nomeCartao: '',
    validadeCartao: '',
    cvv: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNextStep = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Remover mascara de caracteres não numericos para docs e fone
      const onlyNumbers = (str) => str ? str.replace(/\D/g, '') : '';
      
      const dbData = {
        name: formData.nomeEmpresa || formData.nome,
        tradeName: formData.nomeEmpresa || formData.nome,
        document: onlyNumbers(formData.documento),
        email: formData.email,
        phone: onlyNumbers(formData.telefone),
        ownerName: formData.nome,
        ownerEmail: formData.email,
        plan: plan ? (plan.title || 'pro').toLowerCase() : 'pro',
        active: false, // Inativo por padrão, aguardando pagamento
        legalRepresentative: {
            name: formData.nome,
            cpf: onlyNumbers(formData.documento),
            email: formData.email,
            phone: onlyNumbers(formData.telefone)
        },
        responsible: {
            name: formData.nome,
            cpf: onlyNumbers(formData.documento),
            email: formData.email,
            phone: onlyNumbers(formData.telefone),
            role: "Proprietário"
        }
      };

      let currentTenantId = tenantId;

      if (!currentTenantId) {
        // Criar novo Rascunho de Tenant
        const newTenantRef = doc(collection(db, "tenants"));
        currentTenantId = newTenantRef.id;
        
        await setDoc(newTenantRef, {
            ...dbData,
            id: currentTenantId,
            idTenant: currentTenantId,
            createdAt: serverTimestamp(),
            slug: (dbData.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '') || currentTenantId
        });
        setTenantId(currentTenantId);
      } else {
        // Atualizar rascunho existente
        const existingRef = doc(db, "tenants", currentTenantId);
        await updateDoc(existingRef, {
            ...dbData,
            updatedAt: serverTimestamp()
        });
      }

      setStep(2);
    } catch (error) {
      console.error("Erro ao salvar rascunho do tenant (Checkout):", error);
      // Como a rota é pública, as regras do Firestore podem bloquear o salvamento se não estivermos usando signInAnonymously ou regras abertas
      // Se de fato der permissão negada, deixamos avançar para o usuário não ficar travado e lidamos com a criação final na hora de pagar/gerar o user.
      setStep(2); 
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // TODO: Integração com provedor de pagamento (Stripe, PagarMe, etc)
    console.log("Dados do formulário de checkout final:", { ...formData, plano: plan, metodoPagamento });
    alert("Processando pagamento... Iniciando trial de 30 dias!");
    navigate('/login');
  };

  if (!plan) return null;

  const renderPaymentFields = () => {
    if (metodoPagamento === 'CARTAO') {
      return (
        <div className="mt-4 animate-fade-in">
          <Row>
            <Col md={12}>
              <FormGroup className="mb-3">
                <Label style={{ color: '#00f3ff', fontSize: '0.85rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1px' }}>Número do Cartão</Label>
                <Input 
                  type="text" 
                  name="numeroCartao" 
                  placeholder="0000 0000 0000 0000"
                  value={formData.numeroCartao}
                  onChange={handleChange}
                  required
                  className="checkout-input"
                  maxLength={19}
                />
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup className="mb-3">
                <Label style={{ color: '#00f3ff', fontSize: '0.85rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1px' }}>Nome impresso no Cartão</Label>
                <Input 
                  type="text" 
                  name="nomeCartao" 
                  placeholder="Seu nome igual ao cartão"
                  value={formData.nomeCartao}
                  onChange={handleChange}
                  required
                  className="checkout-input"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-3">
                <Label style={{ color: '#00f3ff', fontSize: '0.85rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1px' }}>Validade (MM/YY)</Label>
                <Input 
                  type="text" 
                  name="validadeCartao" 
                  placeholder="MM/AA"
                  value={formData.validadeCartao}
                  onChange={handleChange}
                  required
                  className="checkout-input"
                  maxLength={5}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-3">
                <Label style={{ color: '#00f3ff', fontSize: '0.85rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1px' }}>CVV</Label>
                <Input 
                  type="text" 
                  name="cvv" 
                  placeholder="123"
                  value={formData.cvv}
                  onChange={handleChange}
                  required
                  className="checkout-input"
                  maxLength={4}
                />
              </FormGroup>
            </Col>
          </Row>
        </div>
      );
    }

    if (metodoPagamento === 'PIX') {
      return (
        <div className="mt-4 p-4 text-center animate-fade-in" style={{ backgroundColor: 'rgba(0, 243, 255, 0.05)', border: '1px dashed rgba(0, 243, 255, 0.3)', borderRadius: '12px' }}>
          <i className="mdi mdi-qrcode-scan mb-3" style={{ fontSize: '3rem', color: '#00f3ff' }}></i>
          <h5 style={{ color: '#fff', fontWeight: 'bold' }}>Pagamento via PIX</h5>
          <p style={{ color: '#a6b0cf', fontSize: '0.9rem', margin: 0 }}>
            Ao confirmar, um código copy-e-cola e o QR Code serão gerados para você efetuar o pagamento instantâneo.
          </p>
        </div>
      );
    }

    if (metodoPagamento === 'BOLETO') {
      return (
        <div className="mt-4 p-4 text-center animate-fade-in" style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px dashed rgba(255, 255, 255, 0.2)', borderRadius: '12px' }}>
          <i className="mdi mdi-barcode mb-3" style={{ fontSize: '3rem', color: '#e9ecef' }}></i>
          <h5 style={{ color: '#fff', fontWeight: 'bold' }}>Boleto Bancário</h5>
          <p style={{ color: '#a6b0cf', fontSize: '0.9rem', margin: 0 }}>
            O boleto será emitido após a confirmação e o sistema só será liberado após a compensação (até 3 dias úteis).
          </p>
        </div>
      );
    }

    return null;
  };

  return (
    <React.Fragment>
      <div className="landing-page-container" style={{ margin: 0, padding: 0, overflowX: 'hidden' }}>
        
        {/* Logo Superior Direito Absoluto */}
        <div style={{ position: 'absolute', top: '30px', right: '40px', zIndex: 10 }}>
          <img 
            src={neoLogo} 
            alt="NeoApp Logo" 
            height="35" 
            style={{ filter: 'brightness(0) invert(1)', cursor: 'pointer', opacity: 0.9 }} 
            onClick={() => navigate('/')} 
          />
        </div>

        <div style={{ 
          position: 'relative', 
          minHeight: '100vh', 
          backgroundColor: '#0f1118', // Fundo escuro real pra preencher onde a foto não alcançar
          backgroundImage: `url(${bgImage})`,
          backgroundSize: 'cover', // Ocupa toda a tela, cortando o necessário para preencher
          backgroundRepeat: 'no-repeat',
          backgroundPosition: '100% 15%', // X=100% (direita), Y=15% (puxando a foto levemente pra cima em relação ao topo)
          display: 'flex',
          alignItems: 'stretch',
          overflow: 'hidden'
        }}>
          {/* Dark Overlay reduzido */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(15, 17, 24, 0.35)', // Máscara mais clara
            zIndex: 1
          }}></div>

          <Container fluid className="px-0 position-relative w-100" style={{ zIndex: 2, display: 'flex' }}>
            <Row className="m-0 w-100 align-items-stretch">
              
              {/* Largura do formulário reduzida */}
              <Col lg={7} xl={5} className="p-0 d-flex flex-column">
                <div 
                  className="checkout-panel"
                  style={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(26, 30, 41, 0.85)', 
                    backdropFilter: 'blur(15px)',
                    borderRight: '1px solid rgba(0, 243, 255, 0.15)',
                    padding: 'clamp(30px, 5vw, 60px) clamp(20px, 4vw, 40px)',
                    boxShadow: '20px 0 50px rgba(0,0,0,0.4)',
                  }}
                >
                  
                  {/* Etapa 1: Cadastro */}
                  {step === 1 && (
                    <div className="animate-fade-in" style={{ maxWidth: '500px', width: '100%', margin: '0 auto', position: 'relative' }}>
                      
                      {/* Texto Outline Background */}
                      <div 
                        className="text-uppercase position-absolute" 
                        style={{
                          fontSize: 'clamp(4rem, 8vw, 8rem)', 
                          fontWeight: 'bold', 
                          color: 'transparent',
                          WebkitTextStroke: '1px rgba(0, 243, 255, 0.15)', 
                          top: '-40px',
                          left: '-20px', 
                          zIndex: -1,
                          whiteSpace: 'nowrap',
                          userSelect: 'none',
                          letterSpacing: '5px'
                        }}
                      >
                        DADOS
                      </div>

                      <div className="mb-4 position-relative" style={{ zIndex: 2 }}>
                        <span style={{ color: '#00f3ff', fontWeight: 'bold', letterSpacing: '2px', fontSize: '0.8rem', textTransform: 'uppercase' }}>Etapa 1 de 2</span>
                        <h2 className="mt-2 mb-2 text-uppercase" style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', fontWeight: 'bold', color: '#ffffff', letterSpacing: '-0.5px' }}>
                          Seus <span style={{ color: '#00f3ff' }}>Dados</span>
                        </h2>
                        <p style={{ color: '#a6b0cf', fontSize: '0.95rem', lineHeight: '1.5' }}>
                          Crie sua conta para liberar no plano <strong style={{color: '#fff'}}>{plan.title}</strong>
                        </p>
                      </div>

                      <Form onSubmit={handleNextStep}>
                        <Row>
                          <Col md={12}>
                            <FormGroup className="mb-4">
                              <Label style={{ color: '#00f3ff', fontSize: '0.85rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1px' }}>Nome Completo *</Label>
                              <Input 
                                type="text" name="nome" placeholder="Seu nome" value={formData.nome} onChange={handleChange} required
                                className="checkout-input"
                              />
                            </FormGroup>
                          </Col>

                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label style={{ color: '#00f3ff', fontSize: '0.85rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1px' }}>CPF / CNPJ *</Label>
                              <Input 
                                type="text" name="documento" placeholder="Apenas números" value={formData.documento} onChange={handleChange} required
                                className="checkout-input"
                              />
                            </FormGroup>
                          </Col>
                          
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label style={{ color: '#00f3ff', fontSize: '0.85rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1px' }}>Empresa / Estúdio</Label>
                              <Input 
                                type="text" name="nomeEmpresa" placeholder="Opcional" value={formData.nomeEmpresa} onChange={handleChange}
                                className="checkout-input"
                              />
                            </FormGroup>
                          </Col>

                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label style={{ color: '#00f3ff', fontSize: '0.85rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1px' }}>E-mail *</Label>
                              <Input 
                                type="email" name="email" placeholder="seu@email.com" value={formData.email} onChange={handleChange} required
                                className="checkout-input"
                              />
                            </FormGroup>
                          </Col>

                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label style={{ color: '#00f3ff', fontSize: '0.85rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1px' }}>WhatsApp *</Label>
                              <Input 
                                type="tel" name="telefone" placeholder="(00) 00000-0000" value={formData.telefone} onChange={handleChange} required
                                className="checkout-input"
                              />
                            </FormGroup>
                          </Col>

                          <Col md={12}>
                            <FormGroup className="mb-5">
                              <Label style={{ color: '#00f3ff', fontSize: '0.85rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1px' }}>Senha de Acesso *</Label>
                              <Input 
                                type="password" name="senha" placeholder="Crie uma senha segura" value={formData.senha} onChange={handleChange} required
                                className="checkout-input"
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <button type="submit" className="w-100 checkout-btn" disabled={loading}>
                          {loading ? <Spinner size="sm" style={{ color: '#00f3ff' }} /> : 'Avançar para Pagamento'}
                        </button>
                      </Form>
                    </div>
                  )}

                  {/* Etapa 2: Pagamento */}
                  {step === 2 && (
                    <div className="animate-fade-in" style={{ maxWidth: '500px', width: '100%', margin: '0 auto', position: 'relative' }}>
                      
                      {/* Texto Outline Background */}
                      <div 
                        className="text-uppercase position-absolute" 
                        style={{
                          fontSize: 'clamp(4rem, 8vw, 8rem)', 
                          fontWeight: 'bold', 
                          color: 'transparent',
                          WebkitTextStroke: '1px rgba(0, 243, 255, 0.15)', 
                          top: '-15px',
                          left: '-20px', 
                          zIndex: -1,
                          whiteSpace: 'nowrap',
                          userSelect: 'none',
                          letterSpacing: '5px'
                        }}
                      >
                        PLANO
                      </div>

                      <div className="mb-3 position-relative" style={{ zIndex: 2 }}>
                        <span 
                          onClick={() => setStep(1)} 
                          style={{ color: '#a6b0cf', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                        >
                          <i className="mdi mdi-arrow-left me-1"></i> Voltar aos dados
                        </span>
                      </div>

                      <div className="mb-4 position-relative" style={{ zIndex: 2 }}>
                        <span style={{ color: '#00f3ff', fontWeight: 'bold', letterSpacing: '2px', fontSize: '0.8rem', textTransform: 'uppercase' }}>Etapa 2 de 2</span>
                        <h2 className="mt-1 mb-2 text-uppercase" style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', fontWeight: 'bold', color: '#ffffff', letterSpacing: '-0.5px' }}>
                          Comprar <span style={{ color: '#00f3ff' }}>Plano</span>
                        </h2>
                      </div>

                      <div className="d-flex justify-content-between align-items-center p-3 mb-4 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                        <div>
                          <div style={{ color: '#a6b0cf', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Plano Selecionado</div>
                          <div style={{ color: '#fff', fontSize: '1.2rem', fontWeight: 'bold' }}>{plan.title.toUpperCase()}</div>
                        </div>
                        <div className="text-end">
                          <div style={{ color: '#00f3ff', fontSize: '1.5rem', fontWeight: 'bold' }}>R$ {plan.price}</div>
                          <div style={{ color: '#a6b0cf', fontSize: '0.8rem' }}>{plan.period === '/30 dias' ? 'Grátis agora' : 'Cobrança mensal'}</div>
                        </div>
                      </div>

                      <Form onSubmit={handleSubmit}>
                        
                        <Label style={{ color: '#00f3ff', fontSize: '0.85rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '15px' }}>
                          Método de Pagamento
                        </Label>

                        <div className="payment-methods-grid">
                          {['CARTAO', 'PIX', 'BOLETO'].map((metodo) => (
                            <div 
                              key={metodo}
                              className={`payment-method-card ${metodoPagamento === metodo ? 'active' : ''}`}
                              onClick={() => setMetodoPagamento(metodo)}
                            >
                              <i className={`mdi ${metodo === 'CARTAO' ? 'mdi-credit-card-outline' : metodo === 'PIX' ? 'mdi-qrcode-scan' : 'mdi-barcode'} mb-2 d-block`} style={{ fontSize: '1.5rem' }}></i>
                              {metodo === 'CARTAO' ? 'Cartão' : metodo}
                            </div>
                          ))}
                        </div>

                        {renderPaymentFields()}

                        <div className="mt-5">
                          <button type="submit" className="w-100 checkout-btn">
                            <i className="mdi mdi-lock-outline me-2"></i> Finalizar Assinatura
                          </button>
                          <div className="text-center mt-3">
                            <span style={{ fontSize: '0.75rem', color: '#74788d' }}><i className="mdi mdi-shield-check me-1 text-success"></i> Pagamento 100% seguro. Processado de forma criptografada.</span>
                          </div>
                        </div>
                      </Form>
                    </div>
                  )}
                  
                </div>
              </Col>
            </Row>
          </Container>

          <style>
            {`
              .checkout-input {
                background-color: rgba(15, 17, 24, 0.4) !important;
                border: 1px solid rgba(255, 255, 255, 0.08) !important;
                color: #fff !important;
                border-radius: 10px !important;
                padding: 14px 18px !important;
                font-size: 1rem !important;
                transition: all 0.3s ease !important;
              }
              .checkout-input:focus {
                outline: none !important;
                border-color: #00f3ff !important;
                box-shadow: 0 0 0 1px #00f3ff, inset 0 2px 5px rgba(0,0,0,0.5) !important;
                background-color: rgba(15, 17, 24, 0.7) !important;
              }
              .checkout-input::placeholder {
                color: rgba(255, 255, 255, 0.2) !important;
              }
              .checkout-btn {
                background-color: transparent;
                border: 1px solid #00f3ff;
                color: #00f3ff;
                padding: 16px 30px;
                font-size: 1.1rem;
                font-weight: bold;
                border-radius: 10px;
                text-transform: uppercase;
                letter-spacing: 1px;
                cursor: pointer;
                transition: all 0.3s ease;
                box-shadow: 0 0 15px rgba(0, 243, 255, 0.2), inset 0 0 10px rgba(0, 243, 255, 0.05);
                display: flex;
                align-items: center;
                justify-content: center;
              }
              .checkout-btn:hover {
                background-color: rgba(0, 243, 255, 0.1);
                box-shadow: 0 0 20px rgba(0, 243, 255, 0.4), inset 0 0 15px rgba(0, 243, 255, 0.2);
                transform: translateY(-2px);
              }
              .payment-methods-grid {
                display: grid;
                grid-template-columns: repeat(3, 1fr);
                gap: 15px;
              }
              .payment-method-card {
                background-color: rgba(255,255,255,0.03);
                border: 1px solid rgba(255,255,255,0.08);
                border-radius: 10px;
                padding: 15px 10px;
                text-align: center;
                color: #a6b0cf;
                cursor: pointer;
                transition: all 0.2s;
                font-weight: 500;
                font-size: 0.9rem;
              }
              .payment-method-card:hover {
                background-color: rgba(255,255,255,0.05);
                color: #e9ecef;
              }
              .payment-method-card.active {
                background-color: rgba(0, 243, 255, 0.05);
                border-color: #00f3ff;
                color: #00f3ff;
                box-shadow: 0 0 15px rgba(0,243,255,0.15);
              }
              .animate-fade-in {
                animation: fadeIn 0.4s ease-out;
              }
              @keyframes fadeIn {
                from { opacity: 0; transform: translateY(10px); }
                to { opacity: 1; transform: translateY(0); }
              }
            `}
          </style>
        </div>
      </div>
    </React.Fragment>
  );
};

export default CheckoutPage;
